import type { LocationRaw } from 'src/models';
import { type TagResponse, toTag } from 'src/models/apis/tag/tagResponse';
import type {
  CameraPosition,
  Pivot,
  MovieTag,
  Kilopost,
  MovieGeoIndex,
  Movie,
  MovieFile,
  MovieFileMap,
  MovieGetMovieFiles,
  MovieListCore,
  MovieList,
  RouteSearchMovieList,
} from 'src/models/movie';

export interface PivotResponse {
  movie_id: number;
  tag_id: number;
  updated_at: string;
  created_at: string;
}

export interface MovieTagResponse extends TagResponse {
  pivot: PivotResponse;
}

export interface KilopostResponse {
  angle: number;
  bikou1?: string | null;
  direction: string;
  kp: string;
  kp2?: number;
  kp_calc: string;
  kp_disp?: string | null;
  kp_prefix: string;
  kp_set_id: number;
  kp_uid: string;
  lat: string;
  lon: string;
  place_name: string;
  road_name: string;
  road_name_disp: string;
  road_section_type: string;
}

export interface MovieGeoIndexResponse {
  id: number;
  is_place_filled: boolean;
  kp_uid: string | null;
  lat: string;
  lon: string;
  movie_id: number;
  ts_msec_diff: number;
  velocity?: string | null;
  // kilopost は mtxs/by_device_id（車載器デバッグ）でのみ eager load される。動画検索系では返らないため optional。
  kilopost?: KilopostResponse | null;
}

export interface MovieCoreResponse {
  device_id: string;
  duration: string;
  id: number;
  lat: string;
  lon: string;
  movie_group_id: number;
  start_ts: string;
  tags: MovieTagResponse[];
}

export interface MovieResponse extends MovieCoreResponse {
  car_name: string;
  start_offset_msec: number;
  movie_geo_indices: MovieGeoIndexResponse[];
  end_offset_msec?: number | null;
}

type MovieListCoreResponse = {
  id: string;
  duration: number;
  start_kp_uid: string | null;
  end_kp_uid: string | null;
  start_pt: LocationRaw;
  end_pt: LocationRaw;
  start_ts: string;
  end_ts: string;
  hit_mgi_map: Record<number, number>;
  movies: MovieResponse[];
};

export type MovieListResponse = MovieListCoreResponse & {
  car_name: string;
  device_id: string;
  min_distance: number;
};
export type MovieSearchResponse = MovieListResponse[];

export type RouteSearchMovieListResponse = MovieListCoreResponse;
export type MovieSearchByRoutesResponse = RouteSearchMovieListResponse[];

export const toMovieListCore = (response: MovieListCoreResponse): MovieListCore => ({
  id: response.id,
  duration: response.duration,
  startKpUid: response.start_kp_uid,
  endKpUid: response.end_kp_uid,
  startPt: response.start_pt,
  endPt: response.end_pt,
  startTs: response.start_ts,
  endTs: response.end_ts,
  hitMgiMap: response.hit_mgi_map,
  movies: response.movies.map(toMovie),
});

export const toMovieList = (response: MovieListResponse): MovieList => ({
  ...toMovieListCore(response),
  carName: response.car_name,
  deviceId: response.device_id,
  minDistance: response.min_distance,
});

export const toRouteSearchMovieList = (response: RouteSearchMovieListResponse): RouteSearchMovieList =>
  toMovieListCore(response);

export interface MovieFileResponse {
  id: number;
  movie_id: number;
  movie_path: string;
  camera_position: string;
  signed_url: string;
}

// frontのみでleft,rightが無い動画も存在するため、キーが欠けうることをoptionalで表す
export type MovieFileMapResponse = Partial<Record<CameraPosition, MovieFileResponse>>;

export interface MovieGetMovieFilesResponse {
  movie_file_map: MovieFileMapResponse;
  auth_token: string;
}

export type ContinuedMoviesResponse = MovieResponse[];

export const toPivot = (response: PivotResponse): Pivot => ({
  movieId: response.movie_id,
  tagId: response.tag_id,
  updatedAt: response.updated_at,
  createdAt: response.created_at,
});

export const toMovieTag = (response: MovieTagResponse): MovieTag => ({
  ...toTag(response),
  pivot: toPivot(response.pivot),
});

export const toKilopost = (response: KilopostResponse): Kilopost => ({
  angle: response.angle,
  bikou1: response.bikou1 ?? null,
  direction: response.direction,
  kp: response.kp,
  kp2: response.kp2 ?? null,
  kpCalc: response.kp_calc,
  kpDisp: response.kp_disp ?? null,
  kpPrefix: response.kp_prefix,
  kpSetId: response.kp_set_id,
  kpUid: response.kp_uid,
  location: { lat: response.lat, lon: response.lon },
  placeName: response.place_name,
  roadName: response.road_name,
  roadNameDisp: response.road_name_disp,
  roadSectionType: response.road_section_type,
});

export const toMovieGeoIndex = (response: MovieGeoIndexResponse): MovieGeoIndex => ({
  id: response.id,
  isPlaceFilled: response.is_place_filled,
  kpUid: response.kp_uid,
  location: { lat: response.lat, lon: response.lon },
  movieId: response.movie_id,
  tsMsecDiff: response.ts_msec_diff,
  velocity: response.velocity ?? null,
  kilopost: response.kilopost ? toKilopost(response.kilopost) : null,
});

export const toMovie = (response: MovieResponse): Movie => ({
  deviceId: response.device_id,
  duration: response.duration,
  id: response.id,
  location: { lat: response.lat, lon: response.lon },
  movieGroupId: response.movie_group_id,
  startTs: response.start_ts,
  tags: response.tags.map(toMovieTag),
  carName: response.car_name,
  startOffsetMsec: response.start_offset_msec,
  movieGeoIndices: response.movie_geo_indices.map(toMovieGeoIndex),
  endOffsetMsec: response.end_offset_msec ?? null,
});

export const toMovieFile = (response: MovieFileResponse): MovieFile => ({
  id: response.id,
  movieId: response.movie_id,
  moviePath: response.movie_path,
  cameraPosition: response.camera_position,
  signedUrl: response.signed_url,
});

// レスポンスに含まれるカメラ位置のみを変換する。
// 全キーを直接参照すると、frontのみの動画でundefinedを触って例外になる。
export const toMovieFileMap = (response: MovieFileMapResponse): MovieFileMap => {
  const movieFileMap: Record<string, MovieFile> = {};
  for (const [cameraPosition, movieFile] of Object.entries(response)) {
    if (!movieFile) continue;

    movieFileMap[cameraPosition] = toMovieFile(movieFile);
  }
  return movieFileMap;
};

export const toMovieGetMovieFiles = (response: MovieGetMovieFilesResponse): MovieGetMovieFiles => ({
  movieFileMap: toMovieFileMap(response.movie_file_map),
  authToken: response.auth_token,
});
